import React, { memo } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Typography,
  Paper
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import type { TimeCondition, DayOfWeek, WeekNumber, RuleType } from '../../../../domain/doctorShiftRule';
import {
  DAY_OF_WEEK_LABELS,
  WEEK_NUMBER_LABELS,
  RULE_TYPE_LABELS
} from '../../../../domain/doctorShiftRule';

interface TimeConditionFormProps {
  conditions: TimeCondition[];
  ruleType: RuleType;
  onChange: (conditions: TimeCondition[]) => void;
  onRuleTypeChange: (ruleType: RuleType) => void;
  disabled?: boolean;
  showRuleType?: boolean;
}

const DAYS_OF_WEEK: DayOfWeek[] = [
  'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'
];

const WEEK_NUMBERS: WeekNumber[] = ['all', 1, 2, 3, 4, 5];

const DEFAULT_CONDITION: TimeCondition = {
  weekNumber: 'all',
  dayOfWeek: 'monday',
  startTime: '09:00',
  endTime: '17:00'
};

/**
 * 時間条件フォームコンポーネント
 *
 * - 週選択プルダウン（毎週、第1〜第5）
 * - 曜日選択プルダウン
 * - 開始時間選択
 * - 終了時間選択
 * - ルールタイプ選択
 * - 条件追加・削除機能
 */
const TimeConditionForm: React.FC<TimeConditionFormProps> = memo(({
  conditions,
  ruleType,
  onChange,
  onRuleTypeChange,
  disabled = false,
  showRuleType = true
}) => {
  const handleConditionChange = (
    index: number,
    field: keyof TimeCondition,
    value: unknown
  ) => {
    const newConditions = [...conditions];
    newConditions[index] = {
      ...newConditions[index],
      [field]: value
    };
    onChange(newConditions);
  };

  const handleAddCondition = () => {
    onChange([...conditions, { ...DEFAULT_CONDITION }]);
  };

  const handleRemoveCondition = (index: number) => {
    if (conditions.length > 1) {
      const newConditions = conditions.filter((_, i) => i !== index);
      onChange(newConditions);
    }
  };

  return (
    <Box>
      {/* ルールタイプ選択 */}
      {showRuleType && (
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel id="rule-type-label">ルールタイプ</InputLabel>
          <Select
            labelId="rule-type-label"
            value={ruleType}
            label="ルールタイプ"
            onChange={(e) => onRuleTypeChange(e.target.value as RuleType)}
            disabled={disabled}
          >
            <MenuItem value="assignable">{RULE_TYPE_LABELS.assignable}</MenuItem>
            <MenuItem value="prohibited">{RULE_TYPE_LABELS.prohibited}</MenuItem>
          </Select>
        </FormControl>
      )}

      {/* 時間条件リスト */}
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        時間条件
      </Typography>

      {conditions.map((condition, index) => (
        <Paper
          key={index}
          variant="outlined"
          sx={{ p: 2, mb: 2 }}
        >
          <Grid container spacing={2} alignItems="center">
            {/* 週選択 */}
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel id={`week-label-${index}`}>週</InputLabel>
                <Select
                  labelId={`week-label-${index}`}
                  value={condition.weekNumber}
                  label="週"
                  onChange={(e) =>
                    handleConditionChange(index, 'weekNumber', e.target.value)
                  }
                  disabled={disabled}
                >
                  {WEEK_NUMBERS.map((week) => (
                    <MenuItem key={week} value={week}>
                      {WEEK_NUMBER_LABELS[week]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* 曜日選択 */}
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel id={`day-label-${index}`}>曜日</InputLabel>
                <Select
                  labelId={`day-label-${index}`}
                  value={condition.dayOfWeek}
                  label="曜日"
                  onChange={(e) =>
                    handleConditionChange(index, 'dayOfWeek', e.target.value)
                  }
                  disabled={disabled}
                >
                  {DAYS_OF_WEEK.map((day) => (
                    <MenuItem key={day} value={day}>
                      {DAY_OF_WEEK_LABELS[day]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* 開始時間 */}
            <Grid item xs={6} sm={2}>
              <TextField
                fullWidth
                size="small"
                label="開始"
                type="time"
                value={condition.startTime}
                onChange={(e) =>
                  handleConditionChange(index, 'startTime', e.target.value)
                }
                slotProps={{ inputLabel: { shrink: true } }}
                disabled={disabled}
              />
            </Grid>

            {/* 終了時間 */}
            <Grid item xs={6} sm={2}>
              <TextField
                fullWidth
                size="small"
                label="終了"
                type="time"
                value={condition.endTime}
                onChange={(e) =>
                  handleConditionChange(index, 'endTime', e.target.value)
                }
                slotProps={{ inputLabel: { shrink: true } }}
                disabled={disabled}
              />
            </Grid>

            {/* 削除ボタン */}
            <Grid item xs={12} sm={2}>
              <IconButton
                onClick={() => handleRemoveCondition(index)}
                disabled={disabled || conditions.length <= 1}
                color="error"
                size="small"
              >
                <DeleteIcon />
              </IconButton>
            </Grid>
          </Grid>
        </Paper>
      ))}

      {/* 条件追加ボタン */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
        <IconButton
          onClick={handleAddCondition}
          disabled={disabled}
          color="primary"
          size="small"
        >
          <AddIcon />
        </IconButton>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ ml: 1, alignSelf: 'center' }}
        >
          条件を追加
        </Typography>
      </Box>
    </Box>
  );
});

TimeConditionForm.displayName = 'TimeConditionForm';

export default TimeConditionForm;
